import React from 'react';
import { IActionsDropdown, IActionsTag } from '../../../types/tableDropdown';
import { ENTITY_ACTIVATED_STATE } from '../../../constants/common';
import InteractiveButton from '../../InteractiveButton/InteractiveButton';
import EmptyDataTable from '../../EmptyDataTable/EmptyDataTable';
import { TableProps } from '../../Table/types/TableProps.type';

const plain = ['string', 'number'];

export const EditableStepsTable: React.FC<TableProps> = ({ defaultKeys, values, actionsButtons = [] }) => {
  type DefaultKeys = (typeof defaultKeys)[number];
  type Record = {
    id: string;
  } & {
    [K in DefaultKeys]?: any;
  };

  const isAvailableActions = actionsButtons && actionsButtons.length !== 0;
  const listTableHeads = defaultKeys.map((optionKey, index) => {
    const isStartRadius = index === 0 ? 'rounded-tl-lg' : '';
    const isEndRadius = index === defaultKeys.length - 1 && !isAvailableActions ? 'rounded-tr-lg' : '';
    const isStartPadding = index === 0 ? 'pl-5 rounded-tl-lg' : '';
    const flexVars = index === 0 ? '' : 'items-center justify-center';
    return (
      <th className={`min-w-[120px] ${isStartRadius} ${isEndRadius}`} key={optionKey}>
        <p className={`flex w-full flex-row flex-nowrap ${flexVars} py-1 pr-1 ${isStartPadding}`}>{optionKey}</p>
      </th>
    );
  });

  const getListTableRowByRecord = (record: Record, rows: string[], actions: IActionsDropdown[] = null) => {
    const listRows = rows.map((optionKey, index) => {
      const isStartPadding = index === 0 ? 'pl-5' : '';
      const isEndPadding = index === defaultKeys.length - 1 && !isAvailableActions ? 'pr-5' : '';
      const isRightPadding = index === defaultKeys.length - 1 ? '' : 'pr-2';

      if (typeof record[optionKey] === 'boolean') {
        const activatedState = record[optionKey]
          ? ENTITY_ACTIVATED_STATE.ACTIVATED
          : ENTITY_ACTIVATED_STATE.DEACTIVATED;
        return (
          <td key={`record-${record.id}-${record[optionKey]}-${index}}`}>
            <p
              className={`inline-flex rounded-full bg-opacity-10 px-3 py-1 text-xs font-medium ${
                record[optionKey] ? 'bg-success text-success' : 'bg-danger text-danger'
              }`}
            >
              {activatedState}
            </p>
          </td>
        );
      }

      if (plain.includes(typeof record[optionKey])) {
        return (
          <td key={`record-value-${record[optionKey]}-by-index-${index}}`}>
            <p className={`py-2 ${isStartPadding} ${isEndPadding} ${isRightPadding}`}>{record[optionKey]}</p>
          </td>
        );
      }

      return (
        <td key={`record-value-${record[optionKey]}-by-index-${index}}`}>
          <div className={`${isStartPadding} ${isEndPadding} ${isRightPadding}`}>{record[optionKey]}</div>
        </td>
      );
    });

    return (
      <>
        {listRows}
        {actions && actions.length !== 0 && (
          <td>
            <div className="flex flex-row items-center justify-center gap-x-1">
              {actions.map((action, index) => (
                <InteractiveButton
                  disabled={action.loading}
                  onClick={() => action.onClick(record.id, record)}
                  key={`action-${index}`}
                  customType="min-h-fit min-w-fit max-h-fit max-w-fit border border-foggy-mist rounded active:border-primary cursor-pointer"
                  style={{ padding: '8px' }}
                  as={action?.tag || IActionsTag.Button}
                >
                  {action.icon(record)}
                </InteractiveButton>
              ))}
            </div>
          </td>
        )}
      </>
    );
  };

  const listBody = values.map((value) => {
    const record = value as Record;
    return (
      <tr key={record.id} className="h-[42px] shadow-inset-table-top odd:bg-white even:bg-gray-2 hover:bg-stroke">
        {getListTableRowByRecord(record, defaultKeys, actionsButtons)}
      </tr>
    );
  });

  return (
    <>
      {values.length !== 0 && (
        <div className="c-table h-full overflow-x-auto ">
          <table className="c-table__table w-full">
            <thead className="c-table__thead text-nowrap leading-6 text-white text-xs">
              <tr className="c-table__tr-head bg-primary">
                <>
                  {listTableHeads}
                  {isAvailableActions && (
                    <th className="w-[120px] min-w-[120px] rounded-tr-lg" key="button-actions">
                      <p className="flex flex-row flex-nowrap justify-center py-2">Edit</p>
                    </th>
                  )}
                </>
              </tr>
            </thead>
            <tbody className="c-table__tbody whitespace-nowrap bg-white font-medium text-xs">{listBody}</tbody>
          </table>
        </div>
      )}
      {values.length === 0 && (
        <div className="c-table__empty-table flex flex-auto items-center justify-center">
          <EmptyDataTable />
        </div>
      )}
    </>
  );
};
